import { forwardRef } from 'react';
import type { InvoiceDetail } from '@lib/api/invoices';

interface PrintableInvoiceProps {
  invoice: InvoiceDetail;
  companyInfo?: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
    gst?: string;
  };
  invoiceFooter?: string;
}

export const PrintableInvoice = forwardRef<HTMLDivElement, PrintableInvoiceProps>(
  ({ invoice, companyInfo, invoiceFooter }, ref) => {
    const formatCurrency = (amount: number) => {
      return `$${(amount || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr: string) => {
      return new Date(dateStr).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    };

    const getStatusLabel = (status: string) => {
      const labels: Record<string, string> = {
        draft: 'DRAFT',
        sent: 'SENT',
        viewed: 'VIEWED',
        partial: 'PARTIALLY PAID',
        paid: 'PAID',
        overdue: 'OVERDUE',
        cancelled: 'CANCELLED',
        revised: 'REVISED',
      };
      return labels[status] || status.toUpperCase();
    };

    const isRevision = (invoice.revision_number || 0) > 0;

    // Default company info — real details; the old placeholder fallback could
    // print "Onyx Precision / 123 Business Ave" on a client invoice (audit)
    const company = companyInfo || {
      name: 'OchrePoint Contracting LTD',
      address: 'Duncan & North Cowichan, BC',
      phone: '(250) 510-0677',
      email: 'info@ochrepoint.ca',
    };

    // Interac e-Transfer remittance address (matches the billing@ mail sender)
    const ETRANSFER_EMAIL = 'billing@ochrepoint.ca';

    // Traditional black-on-white print styles
    const colors = {
      black: '#000000',
      border: '#000000',
      lightBorder: '#cccccc',
    };


    return (
      <div
        ref={ref}
        style={{
          width: '8.5in',
          minHeight: '11in',
          padding: '0.5in',
          backgroundColor: '#ffffff',
          fontFamily: 'Times New Roman, serif',
          fontSize: '11pt',
          color: colors.black,
          boxSizing: 'border-box',
          lineHeight: '1.4',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', borderBottom: `2px solid ${colors.black}`, paddingBottom: '20px' }}>
          {/* Company Info */}
          <div>
            {company.logo && (
              <img
                src={company.logo}
                alt={company.name}
                style={{
                  maxHeight: '50px',
                  maxWidth: '180px',
                  marginBottom: '10px',
                  objectFit: 'contain',
                }}
              />
            )}
            <div style={{ fontSize: '18pt', fontWeight: 'bold', color: colors.black, marginBottom: '6px' }}>
              {company.name}
            </div>
            <div style={{ whiteSpace: 'pre-line', color: colors.black, lineHeight: '1.5' }}>
              {company.address}
            </div>
            <div style={{ marginTop: '6px', color: colors.black }}>
              <div>Tel: {company.phone}</div>
              <div>Email: {company.email}</div>
              {company.gst && <div>GST No: {company.gst}</div>}
            </div>
          </div>

          {/* Invoice Title */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '28pt', fontWeight: 'bold', color: colors.black, marginBottom: '8px' }}>
              {isRevision ? 'REVISED INVOICE' : 'INVOICE'}
            </div>
            <div style={{ fontSize: '14pt', fontWeight: 'bold', color: colors.black }}>
              {invoice.invoice_number}
            </div>
            {isRevision && invoice.parent_invoice_id && (
              <div style={{ marginTop: '4px', fontSize: '9pt', color: colors.black }}>
                Replaces: {String(invoice.invoice_number).replace(/-R\d+$/, '')}
              </div>
            )}
            <div style={{ marginTop: '10px', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>
              Status: {getStatusLabel(invoice.status)}
            </div>
          </div>
        </div>

        {/* Bill To & Job Site & Invoice Details */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
          {/* Bill To */}
          <div style={{ flex: '1' }}>
            <div style={{ fontSize: '9pt', fontWeight: 'bold', color: colors.black, marginBottom: '6px', textTransform: 'uppercase' }}>
              Bill To
            </div>
            <div style={{ fontWeight: 'bold', fontSize: '11pt', marginBottom: '4px', color: colors.black }}>
              {invoice.client_name}
            </div>
            {invoice.client_company && (
              <div style={{ color: colors.black }}>{invoice.client_company}</div>
            )}
            {invoice.client_address && (
              <div style={{ color: colors.black, whiteSpace: 'pre-line', marginTop: '4px' }}>
                {invoice.client_address}
              </div>
            )}
            {invoice.client_email && (
              <div style={{ color: colors.black, marginTop: '6px' }}>{invoice.client_email}</div>
            )}
            {invoice.client_phone && (
              <div style={{ color: colors.black }}>{invoice.client_phone}</div>
            )}
          </div>

          {/* Job Site */}
          {invoice.job_site_address && (
            <div style={{ flex: '1', marginLeft: '24px' }}>
              <div style={{ fontSize: '9pt', fontWeight: 'bold', color: colors.black, marginBottom: '6px', textTransform: 'uppercase' }}>
                Job Site
              </div>
              {invoice.job_title && (
                <div style={{ fontWeight: 'bold', fontSize: '11pt', marginBottom: '4px', color: colors.black }}>
                  {invoice.job_title}
                </div>
              )}
              <div style={{ color: colors.black, whiteSpace: 'pre-line' }}>
                {invoice.job_site_address}
              </div>
            </div>
          )}

          {/* Invoice Details */}
          <div style={{ textAlign: 'right' }}>
            <table style={{ marginLeft: 'auto', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Invoice Date:</td>
                  <td style={{ padding: '3px 0', color: colors.black }}>{formatDate(invoice.invoice_date)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Due Date:</td>
                  <td style={{ padding: '3px 0', color: colors.black }}>
                    {formatDate(invoice.due_date)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Payment Terms:</td>
                  <td style={{ padding: '3px 0', color: colors.black }}>Net {invoice.payment_terms}</td>
                </tr>
                {invoice.job_title && !invoice.job_site_address && (
                  <tr>
                    <td style={{ padding: '3px 12px 3px 0', color: colors.black, textAlign: 'right', fontWeight: 'bold' }}>Job:</td>
                    <td style={{ padding: '3px 0', color: colors.black }}>{invoice.job_title}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Line Items Table */}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
          <thead>
            <tr style={{ borderTop: `2px solid ${colors.black}`, borderBottom: `2px solid ${colors.black}` }}>
              <th style={{ padding: '10px 8px', textAlign: 'left', color: colors.black, fontWeight: 'bold', width: '45%' }}>
                Description
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', color: colors.black, fontWeight: 'bold', width: '12%' }}>
                Qty
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'center', color: colors.black, fontWeight: 'bold', width: '10%' }}>
                Unit
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', color: colors.black, fontWeight: 'bold', width: '16%' }}>
                Unit Price
              </th>
              <th style={{ padding: '10px 8px', textAlign: 'right', color: colors.black, fontWeight: 'bold', width: '17%' }}>
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item) => {
              const isDiscount = (item as any).item_type === 'discount';
              const isPercent = (item as any).discount_type === 'percent';

              return (
                <tr key={item.id} style={{ borderBottom: `1px solid ${colors.lightBorder}` }}>
                  <td style={{ padding: '8px', color: colors.black, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                    {item.description}
                    {isDiscount && isPercent && ` (${item.unit_price}%)`}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: colors.black, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                    {isDiscount ? '-' : item.quantity}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', color: colors.black, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                    {isDiscount ? '-' : (item.unit || '-')}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: colors.black, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                    {isDiscount ? '-' : formatCurrency(item.unit_price)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', color: colors.black, fontStyle: isDiscount ? 'italic' : 'normal' }}>
                    {isDiscount ? `-${formatCurrency(Math.abs(item.total))}` : formatCurrency(item.total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '30px' }}>
          <table style={{ width: '280px', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right' }}>Subtotal:</td>
                <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black }}>{formatCurrency(invoice.subtotal)}</td>
              </tr>
              {invoice.gst_amount !== undefined && invoice.gst_amount > 0 ? (
                <>
                  <tr>
                    <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right' }}>GST ({Number(invoice.gst_rate || 0) * 100}%):</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black }}>{formatCurrency(invoice.gst_amount)}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right' }}>PST ({Number(invoice.pst_rate || 0) * 100}%):</td>
                    <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black }}>{formatCurrency(invoice.pst_amount || 0)}</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right' }}>Tax ({invoice.tax_rate}%):</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black }}>{formatCurrency(invoice.tax_amount)}</td>
                </tr>
              )}
              <tr style={{ borderTop: `2px solid ${colors.black}` }}>
                <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '12pt', color: colors.black, textAlign: 'right' }}>Total:</td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 'bold', fontSize: '12pt', color: colors.black }}>
                  {formatCurrency(invoice.total)}
                </td>
              </tr>
              {invoice.holdback_amount !== undefined && invoice.holdback_amount > 0 && (
                <tr style={{ borderTop: `1px dashed ${colors.black}` }}>
                  <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right', fontSize: '10pt', fontStyle: 'italic' }}>
                    Holdback ({Number(invoice.holdback_percent || 0) * 100}%):
                  </td>
                  <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black, fontSize: '10pt', fontStyle: 'italic' }}>
                    -{formatCurrency(invoice.holdback_amount)}
                  </td>
                </tr>
              )}
              {invoice.amount_paid > 0 && (
                <tr>
                  <td style={{ padding: '6px 12px', color: colors.black, textAlign: 'right' }}>Amount Paid:</td>
                  <td style={{ padding: '6px 0', textAlign: 'right', color: colors.black }}>
                    ({formatCurrency(invoice.amount_paid)})
                  </td>
                </tr>
              )}
              <tr style={{ borderTop: `1px solid ${colors.black}` }}>
                <td style={{ padding: '10px 12px', fontWeight: 'bold', fontSize: '12pt', color: colors.black, textAlign: 'right' }}>
                  Balance Due:
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right', fontWeight: 'bold', fontSize: '12pt', color: colors.black }}>
                  {formatCurrency(invoice.balance_due)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Payment History */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '10px', color: colors.black, borderBottom: `1px solid ${colors.black}`, paddingBottom: '4px' }}>
              Payment History
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.black}` }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>Date</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>Method</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>Reference</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '10pt', color: colors.black, fontWeight: 'bold' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.payments.map((payment) => (
                  <tr key={payment.id} style={{ borderBottom: `1px solid ${colors.lightBorder}` }}>
                    <td style={{ padding: '6px 8px', color: colors.black }}>
                      {formatDate(payment.payment_date)}
                    </td>
                    <td style={{ padding: '6px 8px', color: colors.black }}>
                      {payment.payment_method || '-'}
                    </td>
                    <td style={{ padding: '6px 8px', color: colors.black }}>
                      {payment.reference_number || '-'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.black }}>
                      {formatCurrency(payment.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '6px', color: colors.black, borderBottom: `1px solid ${colors.black}`, paddingBottom: '4px' }}>
              Notes
            </div>
            <div style={{ padding: '8px 0', color: colors.black, whiteSpace: 'pre-line' }}>
              {invoice.notes}
            </div>
          </div>
        )}

        {/* Payment Instructions */}
        {invoice.balance_due > 0 && (
          <div style={{ marginBottom: '30px' }}>
            <div style={{ fontSize: '11pt', fontWeight: 'bold', marginBottom: '6px', color: colors.black, borderBottom: `1px solid ${colors.black}`, paddingBottom: '4px' }}>
              Payment Instructions
            </div>
            <div style={{ padding: '8px 0', color: colors.black, lineHeight: '1.6' }}>
              <div>
                Please remit payment by Interac e-Transfer to <strong>{ETRANSFER_EMAIL}</strong>.
              </div>
              <div style={{ marginTop: '4px' }}>
                Include invoice number <strong>{invoice.invoice_number}</strong> in the e-Transfer message.
              </div>
              {company.gst && (
                <div style={{ marginTop: '4px' }}>GST/HST Registration No: {company.gst}</div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ borderTop: `1px solid ${colors.black}`, paddingTop: '15px', textAlign: 'center', color: colors.black, fontSize: '10pt', whiteSpace: 'pre-line' }}>
          {invoiceFooter || (
            <>
              <div style={{ marginBottom: '4px' }}>Thank you for your business!</div>
              <div>Please make payment by the due date. For questions, contact {company.email}</div>
            </>
          )}
        </div>
      </div>
    );
  }
);

PrintableInvoice.displayName = 'PrintableInvoice';
